import React, { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory, useParams } from "react-router-dom"
import { useQuitGuard } from "../../../hooks/useQuitGuard"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faPlus,
	faSitemap,
	faArrowLeft,
	faTrash,
	faHighlighter,
} from "@fortawesome/free-solid-svg-icons"
import { useTarif } from "../../../hooks/useTarif"
import { HTtoTTC, typeNumber } from "../../../utilities/Addons"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles } from "../../styles/styles"
import CollapsibleCardV2 from "../../basics/interface/CollapsibleCardV2"
import CustomModal, {
	SavingCustomModal,
	DeletingCustomModal,
} from "../../basics/interface/CustomModal"
import {
	ATELIER_LIGNE_TYPE_AUTRE,
	ATELIER_LIGNE_TYPE_FOURNITURE,
	ATELIER_LIGNE_TYPE_MAIN_OEUVRE,
	PAYE_PAR_CLIENT,
	getBaseAtelierLigne,
} from "../AtelierLigneV2"
import AtelierLigneRow, {
	AtelierLigneHeader,
	buildColConfig,
} from "../AtelierLigneRow"
import { useSortableLignes } from "../../../hooks/useSortableLignes"
import { useGroups } from "../../../hooks/groups"
import AtelierInventaireModalV2 from "../AtelierInventaireModalV2"
import {
	fetchAtelierModele,
	fetchAtelierModeleReset,
	postAtelierModele,
	postAtelierModeleReset,
	deleteAtelierModele,
	deleteAtelierModeleReset,
} from "../../../store/actions/atelierModeleActions"

// Section de lignes (un type) : composant dédié car useSortableLignes (drag) est un
// hook. Réutilise AtelierLigneRow / AtelierLigneHeader (rendu V2 de la fiche atelier).
const ModeleLigneSection = ({
	lignes,
	type,
	tva,
	isSVP,
	withInventaire,
	updateAtelierLignes,
	updateLigneFull,
	removeAtelierLignes,
	onReorder,
	onAdd,
	onInventaire,
	btnRef,
}) => {
	const { isAdmin } = useGroups()
	const colConfig = useMemo(
		() =>
			buildColConfig({
				numero: lignes.some((l) => l.articleVirtuel > 0),
				valide: false,
				// Master (SVP) : le tarif = prix d'achat → on l'affiche toujours.
				prixAchat: isSVP || isAdmin,
				prixVente: !isSVP,
				rabais: false,
				payePar: false,
				paye: false,
				modifierPrix: false,
			}),
		[lignes, isAdmin, isSVP],
	)
	const ligneIds = useMemo(() => lignes.map((l) => l.id), [lignes])
	const { setNode, getHandleProps, getRowStyle, draggingId } =
		useSortableLignes(ligneIds, (from, to) => onReorder(type, from, to), {
			disabled: false,
		})
	const fakeAtelier = useMemo(
		() => ({ tva, isAssurance: false, id: null }),
		[tva],
	)
	return (
		<>
			{lignes.length > 0 && <AtelierLigneHeader colConfig={colConfig} />}
			{lignes.map((ligne) => (
				<AtelierLigneRow
					key={`al_${ligne.id}`}
					atelierLigne={ligne}
					atelier={fakeAtelier}
					colConfig={colConfig}
					isSVP={isSVP}
					disableLigne={false}
					updateAtelierLignes={updateAtelierLignes}
					updateLigneFull={updateLigneFull}
					removeAtelierLignes={removeAtelierLignes}
					onModifierPrix={null}
					setNode={setNode}
					getHandleProps={getHandleProps}
					getRowStyle={getRowStyle}
					isDragging={draggingId === ligne.id}
				/>
			))}
			<div style={{ display: "flex", gap: 8, marginTop: 10 }}>
				<button
					ref={btnRef}
					type="button"
					style={generalStyles.btnSecondary}
					onClick={() => onAdd(type)}
				>
					<FontAwesomeIcon icon={faPlus} />
				</button>
				{withInventaire && (
					<button
						type="button"
						style={generalStyles.btnSecondary}
						onClick={() => onInventaire(type)}
						title="Depuis l'inventaire"
					>
						<FontAwesomeIcon icon={faSitemap} />
					</button>
				)}
			</div>
		</>
	)
}

// Fiche V2 d'un modèle atelier (gabarit). Pilote le slice redux atelierModeleInfo
// (comme le form legacy) ; chrome V2 (cartes repliables, CustomModal save/delete).
const AtelierModeleFicheV2 = () => {
	const { id } = useParams()
	const dispatch = useDispatch()
	const history = useHistory()
	const { coutHoraire, tva } = useTarif()
	const atelierInventaire = useSelector((state) => state.atelierInventaire)
	const fournitures = useSelector(
		(state) => state.atelierInfo.fournitures || [],
	)
	const masterLabel = useSelector(
		(state) => state.masterReducer.masterEntite.label || "SVP",
	)
	const { atelierModeleInfo } = useSelector((state) => state)
	const { atelierModele, atelierLigneModele, atelierCommentaireModele } =
		atelierModeleInfo
	const postStatus = atelierModeleInfo.postAtelierModele
	const deleteStatus = atelierModeleInfo.deleteAtelierModele

	const [inventaireType, setInventaireType] = useState(null) // type | null
	// Revue 7 (🟠B) : modèle modifié non enregistré → navigation confirmée et
	// jamais de rechargement automatique au deploy (useQuitGuard).
	const { setDirty, setClean, QuitGuard } = useQuitGuard()
	const editer = (action) => {
		setDirty()
		dispatch(action)
	}
	const [saveModal, setSaveModal] = useState(false)
	const [deleteModal, setDeleteModal] = useState(false)

	const btnRefs = {
		[ATELIER_LIGNE_TYPE_MAIN_OEUVRE]: useRef(null),
		[ATELIER_LIGNE_TYPE_FOURNITURE]: useRef(null),
		[ATELIER_LIGNE_TYPE_AUTRE]: useRef(null),
	}
	const refBtnCom = useRef(null)
	const refBtnNote = useRef(null)

	useEffect(() => {
		if (id === "0") dispatch(fetchAtelierModeleReset())
		else dispatch(fetchAtelierModele(id))
	}, [id, dispatch])

	// --- Modèle / lignes / commentaires (actions slice) -------------------
	const updateAtelierModele = (key, value) =>
		editer({
			type: "UPDATE_ATELIERMODELE",
			value: { ...atelierModele, [key]: value },
		})

	const updateAtelierLigne = (value, key, lid) => {
		const al = atelierLigneModele.find((l) => l.id === lid)
		if (!al) return
		editer({
			type: "UPDATE_ATELIERLIGNEMODELE",
			value: { ...al, [key]: value },
		})
	}
	const updateAtelierLigneFull = (value) =>
		editer({ type: "UPDATE_ATELIERLIGNEMODELE", value })
	// AtelierLigneRow passe l'OBJET ligne (pas l'id) → on extrait l'id.
	const removeAtelierLigne = (al) =>
		editer({
			type: "REMOVE_ATELIERLIGNEMODELE",
			value: al && al.id !== undefined ? al.id : al,
		})

	const addAtelierLigne = (type) => {
		const al = getBaseAtelierLigne(type)
		if (type === ATELIER_LIGNE_TYPE_MAIN_OEUVRE) al.prixAchat = coutHoraire
		editer({ type: "UPDATE_ATELIERLIGNEMODELE", value: al })
	}
	const appendModeleLigne = (al) =>
		editer({ type: "UPDATE_ATELIERLIGNEMODELE", value: al })

	// Callbacks du modal inventaire V2 (article stock/virtuel, fourniture, sous-traitant).
	const addLigneInventaire = (article) => {
		const type = inventaireType
		if (Array.isArray(article)) {
			article.forEach((art) =>
				appendModeleLigne({
					...getBaseAtelierLigne(type),
					id: Math.random(),
					label: art.label,
					quantite: art.quantite_recue - art.quantite_utilisee,
					prixVente: art.prix_vente_ht,
					prixAchat: art.prix_achat_ht,
					payePar: PAYE_PAR_CLIENT,
					articleVirtuel: art.id,
				}),
			)
		} else {
			appendModeleLigne({
				...getBaseAtelierLigne(type),
				id: Math.random(),
				label: article.nom,
				prixVente: article.prixVente,
				payePar: PAYE_PAR_CLIENT,
				isHT: false,
				isAchHT: false,
				article: article.id,
			})
		}
		setInventaireType(null)
	}
	const addLigneFourniture = (fourniture) => {
		if (!fournitures.find((f) => f.id === fourniture.id))
			editer({ type: "ADD_FOURNITURE", value: fourniture })
		appendModeleLigne({
			...getBaseAtelierLigne(inventaireType),
			id: Math.random(),
			label: fourniture.label,
			payePar: PAYE_PAR_CLIENT,
			fourniture: fourniture.id,
		})
		setInventaireType(null)
	}
	const addLigneSousTraitantAutre = (sousTraitantId) => {
		appendModeleLigne({
			...getBaseAtelierLigne(inventaireType),
			id: Math.random(),
			payePar: PAYE_PAR_CLIENT,
			sousTraitant: sousTraitantId,
			isAchHT: inventaireType !== ATELIER_LIGNE_TYPE_MAIN_OEUVRE,
		})
		setInventaireType(null)
	}
	// Réordonnancement (useSortableLignes donne des index dans le type) → ids.
	const onReorder = (type, from, to) => {
		const lignes = atelierLigneModele.filter((l) => l.type === type)
		if (!lignes[from] || !lignes[to]) return
		editer({
			type: "REORDER_ALIGNES_MODELE",
			dragId: lignes[from].id,
			dropId: lignes[to].id,
		})
	}

	const updateAtelierCommentaire = (value, key, cid) => {
		const ac = atelierCommentaireModele.find((c) => c.id === cid)
		if (!ac) return
		editer({
			type: "UPDATE_ATELIERCOMMENTAIREMODELE",
			value: { ...ac, [key]: value },
		})
	}
	const removeAtelierCommentaire = (cid) =>
		editer({ type: "REMOVE_ATELIERCOMMENTAIREMODELE", value: cid })
	const addAtelierCommentaire = (isPrinted) =>
		editer({
			type: "UPDATE_ATELIERCOMMENTAIREMODELE",
			value: {
				id: Math.random(),
				texte: "",
				marked: false,
				print: isPrinted,
			},
		})

	// --- Save / delete ----------------------------------------------------
	const save = () => {
		// Master (SVP) : le "tarif" du modèle = somme des prix d'ACHAT (coût interne) ;
		// client : somme des prix de VENTE.
		let total = 0
		atelierLigneModele.forEach((al) => {
			if (atelierModele.svp) {
				total += al.isAchHT
					? al.quantite * HTtoTTC(al.prixAchat, tva)
					: al.quantite * al.prixAchat
			} else {
				total += al.isHT
					? al.quantite * HTtoTTC(al.prixVente, tva)
					: al.quantite * al.prixVente
			}
		})
		dispatch(
			postAtelierModele(
				JSON.stringify({
					atelierModele: { ...atelierModele, total },
					atelierLigneModele,
					atelierCommentaireModele,
				}),
			),
		).then((res) => {
			// Recharge sur l'id réel (ids des lignes définitifs → pas de doublon au resave).
			if (res && res.atelier) {
				setClean()
				history.replace(`/atelier/modeles/${res.atelier.id}`)
				dispatch(fetchAtelierModele(res.atelier.id))
			}
		})
	}
	const confirmDelete = () =>
		dispatch(deleteAtelierModele(JSON.stringify({ id: atelierModele.id })))

	useEffect(() => {
		if (deleteModal && deleteStatus.deleted)
			history.push("/atelier/modeles")
	}, [deleteStatus.deleted, deleteModal, history])

	// --- Rendu des sections (factorisé) -----------------------------------
	const lignesDeType = (type) =>
		atelierLigneModele.filter((al) => al.type === type)

	// Section de lignes (composant ModeleLigneSection pour le hook de drag).
	const sectionProps = (type, withInventaire) => ({
		lignes: lignesDeType(type),
		type,
		tva,
		isSVP: atelierModele.svp,
		withInventaire,
		updateAtelierLignes: updateAtelierLigne,
		updateLigneFull: updateAtelierLigneFull,
		removeAtelierLignes: removeAtelierLigne,
		onReorder,
		onAdd: addAtelierLigne,
		onInventaire: setInventaireType,
		btnRef: btnRefs[type],
	})

	// Commentaires V2 (style CommentRow de la fiche atelier).
	const renderCommentaires = (printed, btnRef, addPrinted) => {
		const coms = atelierCommentaireModele.filter(
			(ac) => ac.print === printed,
		)
		return (
			<>
				{coms.map((ac) => (
					<div
						key={`cc_${ac.id}`}
						style={{
							...generalStyles.obsItem,
							...(ac.marked
								? {
										backgroundColor: COLORS.warningLight,
										borderColor: COLORS.gold,
									}
								: {}),
						}}
					>
						<input
							type="text"
							style={generalStyles.obsInput}
							value={ac.texte}
							onChange={(e) =>
								updateAtelierCommentaire(
									e.target.value,
									"texte",
									ac.id,
								)
							}
						/>
						<button
							type="button"
							title={
								ac.marked ? "Ne plus surligner" : "Surligner"
							}
							style={generalStyles.iconBtn(
								ac.marked ? COLORS.gold : COLORS.textLight,
							)}
							onClick={() =>
								updateAtelierCommentaire(
									!ac.marked,
									"marked",
									ac.id,
								)
							}
						>
							<FontAwesomeIcon icon={faHighlighter} />
						</button>
						<button
							type="button"
							title="Supprimer le commentaire"
							style={generalStyles.iconBtn(COLORS.danger)}
							onClick={() => removeAtelierCommentaire(ac.id)}
						>
							<FontAwesomeIcon icon={faTrash} />
						</button>
					</div>
				))}
				<div style={{ marginTop: 8 }}>
					<button
						ref={btnRef}
						type="button"
						style={generalStyles.btnSecondary}
						onClick={() => addAtelierCommentaire(addPrinted)}
					>
						<FontAwesomeIcon icon={faPlus} />
					</button>
				</div>
			</>
		)
	}

	return (
		<div style={generalStyles.page}>
			{QuitGuard}
			<div style={{ maxWidth: 920, margin: "0 auto" }}>
				{/* En-tête */}
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						marginBottom: 18,
					}}
				>
					<button
						type="button"
						style={generalStyles.btnSecondary}
						onClick={() => history.push("/atelier/modeles")}
					>
						<FontAwesomeIcon
							icon={faArrowLeft}
							style={{ marginRight: 6 }}
						/>
						Modèles
					</button>
					<div style={{ display: "flex", gap: 8 }}>
						{Number.isInteger(atelierModele.id) && (
							<button
								type="button"
								style={generalStyles.btnSecondary}
								onClick={() => setDeleteModal(true)}
							>
								<FontAwesomeIcon icon={faTrash} />
							</button>
						)}
						<button
							type="button"
							style={generalStyles.btnPrimary}
							onClick={() => setSaveModal(true)}
						>
							Enregistrer
						</button>
					</div>
				</div>

				{/* Infos modèle */}
				<div style={{ ...generalStyles.card, marginBottom: 16 }}>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: 16,
						}}
					>
						<div style={{ ...generalStyles.fieldRow, flex: 1 }}>
							<span style={generalStyles.label}>
								Nom du modèle
							</span>
							<input
								style={generalStyles.input}
								value={atelierModele.nom}
								onChange={(e) =>
									updateAtelierModele("nom", e.target.value)
								}
							/>
						</div>
						<label
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: 8,
								fontSize: 13,
								fontWeight: 600,
								color: COLORS.textMuted,
								cursor: "pointer",
								whiteSpace: "nowrap",
							}}
						>
							<input
								type="checkbox"
								checked={atelierModele.svp}
								onChange={() =>
									updateAtelierModele(
										"svp",
										!atelierModele.svp,
									)
								}
							/>
							{`Modèle ${masterLabel}`}
						</label>
					</div>
					{!atelierModele.svp && (
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: 16,
								marginTop: 12,
							}}
						>
							<label
								style={{
									display: "inline-flex",
									alignItems: "center",
									gap: 8,
									fontSize: 13,
									color: COLORS.textMuted,
									cursor: "pointer",
								}}
							>
								<input
									type="checkbox"
									checked={atelierModele.isForfait}
									onChange={() =>
										updateAtelierModele(
											"isForfait",
											!atelierModele.isForfait,
										)
									}
								/>
								Forfait
							</label>
							<div style={{ ...generalStyles.fieldRow, flex: 1 }}>
								<span style={generalStyles.label}>
									Nombre d'heures
								</span>
								<input
									style={generalStyles.input}
									value={atelierModele.nbHeures}
									disabled={!atelierModele.isForfait}
									onChange={(e) =>
										updateAtelierModele(
											"nbHeures",
											e.target.value,
										)
									}
									onKeyDown={(e) => typeNumber(e)}
								/>
							</div>
						</div>
					)}
				</div>

				{/* Travaux */}
				<CollapsibleCardV2 title="Main d'œuvre">
					<ModeleLigneSection
						{...sectionProps(ATELIER_LIGNE_TYPE_MAIN_OEUVRE)}
					/>
				</CollapsibleCardV2>
				<CollapsibleCardV2 title="Fournitures">
					<ModeleLigneSection
						{...sectionProps(ATELIER_LIGNE_TYPE_FOURNITURE, true)}
					/>
				</CollapsibleCardV2>
				<CollapsibleCardV2 title="Autres">
					<ModeleLigneSection
						{...sectionProps(ATELIER_LIGNE_TYPE_AUTRE)}
					/>
				</CollapsibleCardV2>
				<CollapsibleCardV2 title="Commentaires client">
					{renderCommentaires(true, refBtnCom, true)}
				</CollapsibleCardV2>
				<CollapsibleCardV2 title="Notes internes" defaultOpen={false}>
					{renderCommentaires(false, refBtnNote, false)}
				</CollapsibleCardV2>

				{/* Inventaire V2 (ajout d'article/fourniture/sous-traitant) */}
				{inventaireType !== null && (
					<AtelierInventaireModalV2
						closeModal={() => setInventaireType(null)}
						inventaire={atelierInventaire}
						typeAl={inventaireType}
						plaques={null}
						addAtelierLigneInventaire={addLigneInventaire}
						addAtelierLigneFourniture={addLigneFourniture}
						addAtelierLigneSousTraitantAutre={
							addLigneSousTraitantAutre
						}
					/>
				)}

				{/* Confirmation d'enregistrement */}
				{saveModal &&
					(postStatus.saving ||
					postStatus.saved ||
					postStatus.error ? (
						<SavingCustomModal
							post={postStatus}
							onClose={() => {
								setSaveModal(false)
								dispatch(postAtelierModeleReset())
							}}
						/>
					) : (
						<CustomModal
							title="Enregistrer le modèle ?"
							onClose={() => setSaveModal(false)}
							showCloseButton={false}
							buttons={[
								{
									title: "Annuler",
									styleName: "secondary",
									onClick: () => setSaveModal(false),
								},
								{
									title: "Enregistrer",
									styleName: "primary",
									onClick: save,
								},
							]}
						/>
					))}

				{/* Confirmation de suppression */}
				{deleteModal &&
					(deleteStatus.deleting || deleteStatus.error ? (
						<DeletingCustomModal
							status={deleteStatus}
							onClose={() => {
								setDeleteModal(false)
								dispatch(deleteAtelierModeleReset())
							}}
						/>
					) : (
						<CustomModal
							title="Supprimer le modèle ?"
							subtitle={atelierModele.nom}
							onClose={() => setDeleteModal(false)}
							showCloseButton={false}
							buttons={[
								{
									title: "Annuler",
									styleName: "secondary",
									onClick: () => setDeleteModal(false),
								},
								{
									title: "Supprimer",
									styleName: "danger",
									onClick: confirmDelete,
								},
							]}
						>
							<div
								style={{
									color: COLORS.textMuted,
									fontSize: 14,
								}}
							>
								Cette action est définitive.
							</div>
						</CustomModal>
					))}
			</div>
		</div>
	)
}

export default AtelierModeleFicheV2
